// Redemption Hooks
// Real-time hooks for redemption management

import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Redemption } from '../types';
import {
  getRedemptionsByLender,
  getRedemptionsBySeries,
  getAllPendingRedemptions,
  validateRedemptionEligibility,
  RedemptionEligibility,
} from '../services/redemptionService';

/**
 * Get redemptions for a specific lender with real-time updates
 */
export const useRedemptions = (lenderId?: string, realtime: boolean = true) => {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!lenderId) {
      setRedemptions([]);
      setLoading(false);
      return;
    }

    if (realtime) {
      // Real-time listener
      const q = query(
        collection(db, 'redemptions'),
        where('lenderId', '==', lenderId),
        orderBy('requestDate', 'desc')
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const redemptionsList = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            requestDate: doc.data().requestDate?.toDate(),
            redemptionDate: doc.data().redemptionDate?.toDate(),
            lockupEnd: doc.data().lockupEnd?.toDate(),
            paymentDate: doc.data().paymentDate?.toDate(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
          })) as Redemption[];

          setRedemptions(redemptionsList);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching redemptions:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } else {
      // One-time fetch
      const fetchRedemptions = async () => {
        try {
          const data = await getRedemptionsByLender(lenderId);
          setRedemptions(data);
          setError(null);
        } catch (err: any) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      fetchRedemptions();
    }
  }, [lenderId, realtime]);

  return { redemptions, loading, error };
};

/**
 * Get redemptions for a specific series
 */
export const useSeriesRedemptions = (seriesId?: string) => {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!seriesId) {
      setRedemptions([]);
      setLoading(false);
      return;
    }

    const fetchRedemptions = async () => {
      try {
        const data = await getRedemptionsBySeries(seriesId);
        setRedemptions(data);
        setError(null);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRedemptions();
  }, [seriesId]);

  return { redemptions, loading, error };
};

/**
 * Get all pending redemptions (admin view) with real-time updates
 */
export const usePendingRedemptions = (realtime: boolean = true) => {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (realtime) {
      // Real-time listener for pending/processing redemptions
      const q = query(
        collection(db, 'redemptions'),
        where('status', 'in', ['pending', 'processing']),
        orderBy('requestDate', 'asc') // Oldest first
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const redemptionsList = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            requestDate: doc.data().requestDate?.toDate(),
            redemptionDate: doc.data().redemptionDate?.toDate(),
            lockupEnd: doc.data().lockupEnd?.toDate(),
            paymentDate: doc.data().paymentDate?.toDate(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
          })) as Redemption[];

          setRedemptions(redemptionsList);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching pending redemptions:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } else {
      // One-time fetch
      const fetchRedemptions = async () => {
        try {
          const data = await getAllPendingRedemptions();
          setRedemptions(data);
          setError(null);
        } catch (err: any) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      fetchRedemptions();
    }
  }, [realtime]);

  return { redemptions, loading, error };
};

/**
 * Get all redemptions (admin view) with real-time updates
 */
export const useAllRedemptions = (realtime: boolean = true) => {
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (realtime) {
      // Real-time listener for all redemptions
      const q = query(
        collection(db, 'redemptions'),
        orderBy('requestDate', 'desc')
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const redemptionsList = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            requestDate: doc.data().requestDate?.toDate(),
            redemptionDate: doc.data().redemptionDate?.toDate(),
            lockupEnd: doc.data().lockupEnd?.toDate(),
            paymentDate: doc.data().paymentDate?.toDate(),
            createdAt: doc.data().createdAt?.toDate(),
            updatedAt: doc.data().updatedAt?.toDate(),
          })) as Redemption[];

          setRedemptions(redemptionsList);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching all redemptions:', err);
          setError(err.message);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    }
  }, [realtime]);

  return { redemptions, loading, error };
};

/**
 * Check if a series is eligible for redemption
 */
export const useRedemptionEligibility = (seriesId?: string) => {
  const [eligibility, setEligibility] = useState<RedemptionEligibility | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!seriesId) {
      setEligibility(null);
      setLoading(false);
      return;
    }

    const checkEligibility = async () => {
      try {
        const result = await validateRedemptionEligibility(seriesId);
        setEligibility(result);
        setError(null);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    checkEligibility();
  }, [seriesId]);

  return { eligibility, loading, error };
};

/**
 * Get count of pending redemptions (for dashboard metrics)
 */
export const usePendingRedemptionsCount = () => {
  const { redemptions, loading } = usePendingRedemptions(true);
  return { count: redemptions.length, loading };
};
